import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Footer from '../components/Footer';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://templimizeai-coursecreatorvault.onrender.com/api';

type Step = 'niche' | 'topics' | 'configure' | 'generate' | 'preview';

interface CourseOptions {
  duration: 'short' | 'medium' | 'long';
  level: 'beginner' | 'intermediate' | 'advanced' | 'all';
  format: 'video' | 'text' | 'mixed';
}

interface TrendingTopic {
  title: string;
  reason: string;
  audience: string;
  demand: string;
}

const AICourseCreator: React.FC = () => {
  const { token, user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('niche');
  const [niche, setNiche] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [customTopic, setCustomTopic] = useState('');
  const [trendingTopics, setTrendingTopics] = useState<TrendingTopic[]>([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [options, setOptions] = useState<CourseOptions>({
    duration: 'medium',
    level: 'beginner',
    format: 'video'
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [generating, setGenerating] = useState(false);
  const [courseOutline, setCourseOutline] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const handleFetchTrendingTopics = async () => {
    if (!niche.trim()) {
      toast.warning('Please enter a niche first!');
      return;
    }

    setLoadingTopics(true);
    try {
      const response = await axios.post(
        `${API_URL}/ai/trending-topics`,
        { niche },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setTrendingTopics(response.data.topics || []);
      setStep('topics');
      toast.success('Trending topics generated!');
    } catch (error: any) {
      console.error('Error fetching trending topics:', error);
      toast.error(error.response?.data?.error || 'Failed to fetch trending topics');
    } finally {
      setLoadingTopics(false);
    }
  };

  const handleGenerateCourse = async () => {
    const topic = selectedTopic || customTopic;
    if (!topic.trim()) {
      toast.warning('Please select or enter a course topic!');
      return;
    }

    setGenerating(true);
    setStep('generate');

    try {
      const response = await axios.post(
        `${API_URL}/ai/generate-course`,
        {
          topic,
          niche,
          options
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setCourseOutline(response.data.courseOutline);
      setStep('preview');
      toast.success('Course outline generated successfully!');
    } catch (error: any) {
      console.error('Error generating course:', error);
      toast.error(error.response?.data?.error || 'Failed to generate course outline');
      setStep('configure');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveCourse = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/ai/save-course`,
        { courseOutline: { ...courseOutline, niche } },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success('Course saved successfully!');
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Error saving course:', error);
      toast.error(error.response?.data?.error || 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const renderNicheStep = () => (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-12">
        <div className="inline-block p-4 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full mb-6">
          <svg className="w-16 h-16 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-4">
          Let's Create Your Course with AI
        </h2>
        <p className="text-xl text-gray-600">
          Tell me your niche and I'll help you discover trending topics and build a complete course
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
        <label className="block text-lg font-semibold text-gray-900 mb-3">
          What's your course niche?
        </label>
        <input
          type="text"
          value={niche}
          onChange={(e) => setNiche(e.target.value)}
          placeholder="e.g., Digital Marketing, Web Development, Fitness Training..."
          className="w-full px-6 py-4 text-lg border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-600 focus:border-transparent transition"
        />

        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleFetchTrendingTopics}
            disabled={loadingTopics || !niche.trim()}
            className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingTopics ? 'Finding Trending Topics...' : '✨ Discover Trending Topics'}
          </button>

          <button
            onClick={() => {
              if (!niche.trim()) {
                toast.warning('Please enter a niche first!');
                return;
              }
              setStep('configure');
            }}
            className="flex-1 border-2 border-purple-600 text-purple-600 py-4 rounded-xl font-bold text-lg hover:bg-purple-50 transition"
          >
            I Have My Own Topic →
          </button>
        </div>
      </div>
    </div>
  );

  const renderTopicsStep = () => (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Trending Topics in {niche}
        </h2>
        <p className="text-gray-600">
          Pick a hot topic or skip to create your own
        </p>
      </div>

      <div className="grid gap-6 mb-8">
        {trendingTopics.map((topic, idx) => (
          <div
            key={idx}
            onClick={() => {
              setSelectedTopic(topic.title);
              setCustomTopic('');
            }}
            className={`bg-white rounded-xl shadow-lg p-6 border-2 cursor-pointer transition transform hover:scale-102 hover:shadow-xl ${
              selectedTopic === topic.title
                ? 'border-purple-600 bg-purple-50'
                : 'border-gray-200'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-xl font-bold text-gray-900 flex-1">{topic.title}</h3>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                topic.demand === 'High'
                  ? 'bg-green-100 text-green-700'
                  : topic.demand === 'Medium'
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {topic.demand} Demand
              </span>
            </div>
            <p className="text-gray-600 mb-3">{topic.reason}</p>
            <p className="text-sm text-purple-600 font-semibold">
              🎯 Target: {topic.audience}
            </p>
          </div>
        ))}
      </div>

      <div className="flex gap-4">
        <button
          onClick={() => setStep('niche')}
          className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:border-gray-400 transition"
        >
          ← Back
        </button>
        <button
          onClick={() => setStep('configure')}
          disabled={!selectedTopic}
          className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-bold hover:shadow-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue with Selected Topic →
        </button>
      </div>
    </div>
  );

  const renderConfigureStep = () => (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Configure Your Course
        </h2>
        <p className="text-gray-600">
          Let's customize the course structure
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 space-y-8">
        {/* Custom Topic Input */}
        {!selectedTopic && (
          <div>
            <label className="block text-lg font-semibold text-gray-900 mb-3">
              Course Topic
            </label>
            <input
              type="text"
              value={customTopic}
              onChange={(e) => setCustomTopic(e.target.value)}
              placeholder="Enter your course topic..."
              className="w-full px-6 py-4 text-lg border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-600 focus:border-transparent"
            />
          </div>
        )}

        {selectedTopic && (
          <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4">
            <p className="text-sm text-purple-700 font-semibold mb-1">Selected Topic:</p>
            <p className="text-lg font-bold text-purple-900">{selectedTopic}</p>
          </div>
        )}

        {/* Duration */}
        <div>
          <label className="block text-lg font-semibold text-gray-900 mb-3">
            Course Duration
          </label>
          <div className="grid grid-cols-3 gap-4">
            {[
              { value: 'short', label: 'Short', desc: '2-3 hours' },
              { value: 'medium', label: 'Medium', desc: '5-8 hours' },
              { value: 'long', label: 'Long', desc: '10-20 hours' }
            ].map((item) => (
              <button
                key={item.value}
                onClick={() => setOptions({ ...options, duration: item.value as any })}
                className={`p-4 rounded-xl border-2 transition ${
                  options.duration === item.value
                    ? 'border-purple-600 bg-purple-50 text-purple-900'
                    : 'border-gray-300 text-gray-700 hover:border-purple-300'
                }`}
              >
                <div className="font-bold">{item.label}</div>
                <div className="text-sm">{item.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Level */}
        <div>
          <label className="block text-lg font-semibold text-gray-900 mb-3">
            Target Level
          </label>
          <div className="grid grid-cols-4 gap-4">
            {['beginner', 'intermediate', 'advanced', 'all'].map((level) => (
              <button
                key={level}
                onClick={() => setOptions({ ...options, level: level as any })}
                className={`p-4 rounded-xl border-2 transition capitalize ${
                  options.level === level
                    ? 'border-purple-600 bg-purple-50 text-purple-900'
                    : 'border-gray-300 text-gray-700 hover:border-purple-300'
                }`}
              >
                {level}
              </button>
            ))}
          </div>
        </div>

        {/* Format */}
        <div>
          <label className="block text-lg font-semibold text-gray-900 mb-3">
            Content Format
          </label>
          <div className="grid grid-cols-3 gap-4">
            {[
              { value: 'video', label: 'Video', icon: '🎥' },
              { value: 'text', label: 'Text', icon: '📝' },
              { value: 'mixed', label: 'Mixed', icon: '🎬' }
            ].map((item) => (
              <button
                key={item.value}
                onClick={() => setOptions({ ...options, format: item.value as any })}
                className={`p-4 rounded-xl border-2 transition ${
                  options.format === item.value
                    ? 'border-purple-600 bg-purple-50 text-purple-900'
                    : 'border-gray-300 text-gray-700 hover:border-purple-300'
                }`}
              >
                <div className="text-2xl mb-1">{item.icon}</div>
                <div className="font-bold">{item.label}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-4 mt-8">
        <button
          onClick={() => setStep(selectedTopic ? 'topics' : 'niche')}
          className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:border-gray-400 transition"
        >
          ← Back
        </button>
        <button
          onClick={handleGenerateCourse}
          disabled={!selectedTopic && !customTopic.trim()}
          className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-bold text-lg hover:shadow-xl transition disabled:opacity-50"
        >
          🚀 Generate Course with AI
        </button>
      </div>
    </div>
  );

  const renderGenerateStep = () => (
    <div className="max-w-2xl mx-auto text-center">
      <div className="bg-white rounded-2xl shadow-xl p-12 border border-gray-200">
        <div className="inline-block w-20 h-20 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-6"></div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          AI is Creating Your Course...
        </h2>
        <p className="text-gray-600 mb-4">
          Our AI is analyzing your niche, structuring modules, planning lessons, and crafting the perfect course outline.
        </p>
        <p className="text-sm text-purple-600 font-semibold">
          This usually takes 20-30 seconds ⚡
        </p>
      </div>
    </div>
  );

  const renderPreviewStep = () => (
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Your Course Outline is Ready! 🎉
        </h2>
        <p className="text-gray-600">
          Review and customize before saving
        </p>
      </div>

      {courseOutline && (
        <div className="space-y-6">
          {/* Course Header */}
          <div className="bg-gradient-to-br from-purple-600 to-pink-600 text-white rounded-2xl shadow-xl p-8">
            <h1 className="text-3xl font-bold mb-4">{courseOutline.title}</h1>
            <p className="text-purple-100 mb-4">{courseOutline.description}</p>
            <div className="flex flex-wrap gap-3">
              <span className="px-4 py-2 bg-white bg-opacity-20 rounded-full text-sm font-semibold">
                🎯 {courseOutline.targetAudience}
              </span>
              <span className="px-4 py-2 bg-white bg-opacity-20 rounded-full text-sm font-semibold">
                📚 {courseOutline.modules?.length || 0} Modules
              </span>
              <span className="px-4 py-2 bg-white bg-opacity-20 rounded-full text-sm font-semibold">
                ⏱️ {options.duration}
              </span>
            </div>
          </div>

          {/* Learning Objectives */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Learning Objectives</h3>
            <ul className="space-y-2">
              {courseOutline.learningObjectives?.map((obj: string, idx: number) => (
                <li key={idx} className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">{obj}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Modules */}
          <div className="space-y-4">
            {courseOutline.modules?.map((module: any, moduleIdx: number) => (
              <div key={moduleIdx} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 border-b border-gray-200">
                  <h4 className="text-xl font-bold text-gray-900 mb-2">
                    Module {moduleIdx + 1}: {module.title}
                  </h4>
                  <p className="text-gray-600 mb-3">{module.description}</p>
                  <div className="flex items-center text-sm text-purple-600 font-semibold">
                    <span>{module.lessons?.length || 0} Lessons</span>
                  </div>
                </div>

                <div className="p-6">
                  <div className="space-y-3">
                    {module.lessons?.map((lesson: any, lessonIdx: number) => (
                      <div key={lessonIdx} className="flex items-start p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                          <span className="text-purple-600 font-bold text-sm">{lessonIdx + 1}</span>
                        </div>
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 mb-1">{lesson.title}</h5>
                          <p className="text-sm text-gray-600 mb-2">{lesson.description}</p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>⏱️ {lesson.duration} min</span>
                            <span>📄 {lesson.contentType}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-4 mt-8 sticky bottom-4 bg-white p-4 rounded-xl shadow-xl border border-gray-200">
        <button
          onClick={() => setStep('configure')}
          className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:border-gray-400 transition"
        >
          ← Regenerate
        </button>
        <button
          onClick={handleSaveCourse}
          disabled={saving}
          className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-xl font-bold text-lg hover:shadow-xl transition disabled:opacity-50"
        >
          {saving ? 'Saving...' : '💾 Save Course to Dashboard'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div
              onClick={() => navigate('/dashboard')}
              className="cursor-pointer"
            >
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent leading-tight">
                The Course Creator's Vault
              </h1>
              <p className="text-[10px] sm:text-xs text-gray-500">by Templimize AI</p>
            </div>
            <div className="text-sm text-gray-600">
              Logged in as <span className="font-semibold">{user?.name}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            {[
              { id: 'niche', label: 'Niche' },
              { id: 'topics', label: 'Topics' },
              { id: 'configure', label: 'Configure' },
              { id: 'generate', label: 'Generate' },
              { id: 'preview', label: 'Preview' }
            ].map((s, idx) => {
              const stepOrder = ['niche', 'topics', 'configure', 'generate', 'preview'];
              const currentIdx = stepOrder.indexOf(step);
              const thisIdx = stepOrder.indexOf(s.id);
              const isActive = thisIdx === currentIdx;
              const isCompleted = thisIdx < currentIdx;

              return (
                <div key={s.id} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition ${
                      isActive
                        ? 'bg-purple-600 text-white'
                        : isCompleted
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}>
                      {isCompleted ? '✓' : idx + 1}
                    </div>
                    <div className={`text-xs mt-2 font-semibold ${
                      isActive ? 'text-purple-600' : 'text-gray-500'
                    }`}>
                      {s.label}
                    </div>
                  </div>
                  {idx < 4 && (
                    <div className={`flex-1 h-1 mx-2 ${
                      isCompleted ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-12 px-4 sm:px-6 lg:px-8">
        {step === 'niche' && renderNicheStep()}
        {step === 'topics' && renderTopicsStep()}
        {step === 'configure' && renderConfigureStep()}
        {step === 'generate' && renderGenerateStep()}
        {step === 'preview' && renderPreviewStep()}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AICourseCreator;
